import React, { useState, useMemo } from 'react';
import { 
  X, 
  Search, 
  Filter, 
  TrendingUp, 
  TrendingDown, 
  ArrowRightLeft, 
  Calendar,
  ChevronDown,
  Download,
  Clock
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Transaction, Account } from '../types';
import { format, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import { safeFormat } from '../utils/dateUtils';
import { getInstitutionById } from '../constants/institutions';
import { useFirebase } from './FirebaseProvider';
import { AccountingAccount, JournalEntryType, JournalEntryLine } from '../types';
import { subscribeToAccountingAccounts } from '../services/accountingService';
import { EntryFormModal } from './accounting/EntryFormModal';
import { linkTransactionToJournalEntry, togglePendingInvoice } from '../services/financeService';

interface TransactionHistoryProps {
  transactions: Transaction[];
  accounts: Account[];
  onClose: () => void;
  onEdit: (tx: Transaction) => void;
}

export const TransactionHistory: React.FC<TransactionHistoryProps> = ({ 
  transactions, 
  accounts, 
  onClose, 
  onEdit 
}) => {
  const { effectiveUserId } = useFirebase();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'ALL' | 'INCOME' | 'EXPENSE' | 'TRANSFER'>('ALL');
  const [filterAccount, setFilterAccount] = useState<string>('ALL');
  const [filterPending, setFilterPending] = useState<boolean>(false);
  
  const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null);

  const [accountingAccounts, setAccountingAccounts] = useState<AccountingAccount[]>([]);
  const [settlingTransfer, setSettlingTransfer] = useState<Transaction | null>(null);
  const [proposedEntry, setProposedEntry] = useState<any>(null);

  React.useEffect(() => {
    if (!dateRange) {
      if (transactions.length > 0) {
        const maxTime = Math.max(...transactions.map(t => new Date(t.date).getTime()));
        const maxD = new Date(maxTime);
        const startD = new Date(maxTime);
        startD.setDate(startD.getDate() - 30);
        
        setDateRange({
          start: startD.toISOString().split('T')[0],
          end: maxD.toISOString().split('T')[0]
        });
      } else {
         const today = new Date();
         const past = new Date();
         past.setDate(today.getDate() - 30);
         setDateRange({
          start: past.toISOString().split('T')[0],
          end: today.toISOString().split('T')[0]
        });
      }
    }
  }, [transactions, dateRange]);

  React.useEffect(() => {
    if (effectiveUserId) {
      return subscribeToAccountingAccounts(effectiveUserId, setAccountingAccounts);
    }
  }, [effectiveUserId]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  const filteredTransactions = useMemo(() => {
    return transactions.filter(tx => {
      const matchesSearch = tx.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'ALL' || tx.type === filterType;
      const matchesAccount = filterAccount === 'ALL' || tx.details.some(d => d.accountId === filterAccount);
      const matchesPending = !filterPending || tx.pendingInvoice;
      
      let matchesDate = true;
      if (dateRange) {
        const txDate = tx.date.split('T')[0];
        matchesDate = txDate >= dateRange.start && txDate <= dateRange.end;
      }

      return matchesSearch && matchesType && matchesAccount && matchesPending && matchesDate;
    });
  }, [transactions, searchTerm, filterType, filterAccount, filterPending, dateRange]);

  const handleExport = (exportFormat: 'csv' | 'txt') => {
    interface ExportRow {
      date: string;
      description: string;
      amount: number;
      accountType: string;
      accountName: string;
      institutionName: string;
      originalDate: Date;
    }

    const rows: ExportRow[] = [];

    filteredTransactions.forEach(tx => {
      const txDate = new Date(tx.date);
      const formattedDate = format(txDate, 'dd/MM/yyyy');

      if (tx.type === 'TRANSFER') {
        const debitDetail = tx.details.find(d => d.type === 'DEBIT');
        const creditDetail = tx.details.find(d => d.type === 'CREDIT');
        
        if (debitDetail && creditDetail) {
          const sourceAccount = accounts.find(a => a.id === debitDetail.accountId);
          const destAccount = accounts.find(a => a.id === creditDetail.accountId);
          
          const sourceName = sourceAccount?.name || 'Desconocida';
          const destName = destAccount?.name || 'Desconocida';
          const sourceInst = sourceAccount?.institutionId ? getInstitutionById(sourceAccount.institutionId)?.name || '' : '';
          const destInst = destAccount?.institutionId ? getInstitutionById(destAccount.institutionId)?.name || '' : '';
          
          const baseDesc = `Transferencias desde ${sourceName} a ${destName}`;

          // Line 1: Outflow
          const sourceSuffix = sourceInst ? `${sourceName} - ${sourceInst}` : sourceName;
          rows.push({
            date: formattedDate,
            description: `${baseDesc} - ${sourceSuffix}`,
            amount: -debitDetail.amount,
            accountType: sourceAccount?.type || 'OTROS',
            accountName: sourceName,
            institutionName: sourceInst,
            originalDate: txDate
          });

          // Line 2: Inflow
          const destSuffix = destInst ? `${destName} - ${destInst}` : destName;
          rows.push({
            date: formattedDate,
            description: `${baseDesc} - ${destSuffix}`,
            amount: creditDetail.amount,
            accountType: destAccount?.type || 'OTROS',
            accountName: destName,
            institutionName: destInst,
            originalDate: txDate
          });
        }
      } else {
        tx.details.forEach(detail => {
          const account = accounts.find(a => a.id === detail.accountId);
          const accountName = account?.name || 'Desconocida';
          const institutionName = account?.institutionId ? getInstitutionById(account.institutionId)?.name || '' : '';
          
          const descSuffix = institutionName ? `${accountName} - ${institutionName}` : accountName;
          
          rows.push({
            date: formattedDate,
            description: `${tx.description} - ${descSuffix}`,
            amount: tx.type === 'EXPENSE' ? -detail.amount : detail.amount,
            accountType: account?.type || 'OTROS',
            accountName: accountName,
            institutionName: institutionName,
            originalDate: txDate
          });
        });
      }
    });

    const accountTypeWeight: Record<string, number> = {
      'EFECTIVO': 1,
      'BANCOS': 2,
      'OTROS': 3,
      'TARJETA DE CREDITO': 4,
    };

    rows.sort((a, b) => {
      // 1. Ordenar por tipo de cuenta
      const weightA = accountTypeWeight[a.accountType] || 99;
      const weightB = accountTypeWeight[b.accountType] || 99;
      if (weightA !== weightB) {
        return weightA - weightB;
      }
      
      // 2. Ordenar por nombre de cuenta
      const nameCompare = a.accountName.localeCompare(b.accountName);
      if (nameCompare !== 0) {
        return nameCompare;
      }
      
      // 3. Ordenar por nombre de banco (institución)
      const bankCompare = a.institutionName.localeCompare(b.institutionName);
      if (bankCompare !== 0) {
        return bankCompare;
      }
      
      // 4. Ordenar por fecha
      const dateCompare = a.originalDate.getTime() - b.originalDate.getTime();
      if (dateCompare !== 0) {
        return dateCompare;
      }
      
      // 5. Ordenar por descripción
      return a.description.localeCompare(b.description);
    });

    const separator = exportFormat === 'csv' ? ',' : '\t';
    const exportHeader = ['FECHA', 'DESCRIPCIÓN', 'IMPORTE'];
    const exportContent = [
      exportHeader.join(separator),
      ...rows.map(row => {
        const safeDesc = exportFormat === 'csv' 
          ? `"${row.description.replace(/"/g, '""')}"`
          : row.description.replace(/\t/g, ' '); // remove tabs for txt to avoid breaking columns
        return `${row.date}${separator}${safeDesc}${separator}${row.amount}`;
      })
    ].join('\n');

    const mimeType = exportFormat === 'csv' ? 'text/csv;charset=utf-8;' : 'text/plain;charset=utf-8;';
    const extension = exportFormat === 'csv' ? 'csv' : 'txt';

    // Agregamos el BOM (\uFEFF) para que Excel reconozca correctamente el encoding UTF-8
    const blob = new Blob(['\uFEFF' + exportContent], { type: mimeType });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `transacciones_${format(new Date(), 'yyyyMMdd_HHmmss')}.${extension}`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-zinc-900 border border-zinc-800 w-full max-w-4xl max-h-[90vh] rounded-3xl overflow-hidden flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="shrink-0 p-6 border-b border-zinc-800 bg-zinc-800/30 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Calendar size={20} className="text-indigo-400" />
              Historial de Transacciones
            </h2>
            <p className="text-xs text-zinc-500 mt-1">
              {filteredTransactions.length} transacciones encontradas
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-xl transition-colors text-zinc-400 hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        {/* Filters */}
        <div className="shrink-0 p-4 bg-zinc-900/50 border-b border-zinc-800 space-y-4">
          <div className="flex flex-col md:flex-row gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
              <input
                type="text"
                placeholder="Buscar por descripción..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl pl-10 pr-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all"
              />
            </div>
            
            {/* Date Range Filters */}
            <div className="flex gap-2 flex-wrap">
              <input
                type="date"
                value={dateRange?.start || ''}
                onChange={(e) => setDateRange(prev => prev ? { ...prev, start: e.target.value } : { start: e.target.value, end: new Date().toISOString().split('T')[0] })}
                className="bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all text-zinc-300"
                title="Fecha inicial"
              />
              <input
                type="date"
                value={dateRange?.end || ''}
                onChange={(e) => setDateRange(prev => prev ? { ...prev, end: e.target.value } : { start: new Date().toISOString().split('T')[0], end: e.target.value })}
                className="bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all text-zinc-300"
                title="Fecha final"
              />
            </div>

            <div className="flex gap-2 flex-wrap">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                className="bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all appearance-none cursor-pointer"
              >
                <option value="ALL">Todos los Tipos</option>
                <option value="INCOME">Ingresos</option>
                <option value="EXPENSE">Gastos</option>
                <option value="TRANSFER">Transferencias</option>
              </select>
              <select
                value={filterAccount}
                onChange={(e) => setFilterAccount(e.target.value)}
                className="bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all appearance-none cursor-pointer"
              >
                <option value="ALL">Todas las Cuentas</option>
                {accounts.map(acc => {
                  const instName = acc.institutionId ? getInstitutionById(acc.institutionId)?.name : '';
                  const displayName = instName ? `${acc.name} - ${instName}` : acc.name;
                  return (
                    <option key={acc.id} value={acc.id}>{displayName}</option>
                  );
                })}
              </select>
              <button
                onClick={() => setFilterPending(!filterPending)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                  filterPending
                    ? "bg-amber-500 text-black shadow-lg shadow-amber-500/20"
                    : "bg-zinc-950 border border-zinc-800 text-zinc-400 hover:text-white"
                }`}
              >
                <Clock size={16} />
                Pendientes
              </button>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
          <div className="space-y-2">
            {filteredTransactions.length === 0 ? (
              <div className="text-center py-20 text-zinc-500">
                No se encontraron transacciones con los filtros aplicados.
              </div>
            ) : (
              filteredTransactions.map((tx) => (
                  <div
                    key={tx.id}
                    className="w-full p-4 bg-zinc-950/50 hover:bg-zinc-800/50 border border-zinc-800/50 rounded-2xl transition-all flex items-center justify-between group text-left cursor-pointer"
                    onClick={() => onEdit(tx)}
                  >
                    <div className="flex items-center gap-4 flex-1 overflow-hidden">
                    <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center border ${
                      tx.type === 'INCOME' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500' :
                      tx.type === 'EXPENSE' ? 'bg-red-500/10 border-red-500/20 text-red-500' :
                      'bg-blue-500/10 border-blue-500/20 text-blue-500'
                    }`}>
                      {tx.type === 'INCOME' ? <TrendingUp size={18} /> :
                       tx.type === 'EXPENSE' ? <TrendingDown size={18} /> :
                       <ArrowRightLeft size={18} />}
                    </div>
                    <div className="min-w-0 pr-4">
                      <div className="font-medium text-sm truncate flex items-center gap-2">
                        {tx.description}
                        {tx.pendingInvoice && (
                          <span className="shrink-0 bg-amber-500/20 text-amber-500 text-[9px] px-1.5 py-0.5 rounded-md uppercase tracking-wider font-bold">
                            Pendiente
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-zinc-500 flex items-center gap-2 truncate">
                        <span>{safeFormat(tx.date, "d 'de' MMMM, yyyy", 'Invalid Date')}</span>
                        <span className="w-1 h-1 bg-zinc-700 rounded-full shrink-0" />
                        <span className="truncate">{tx.details.map(d => accounts.find(a => a.id === d.accountId)?.name || 'Cuenta Ext.').join(' ➔ ')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-4 shrink-0">
                    <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (effectiveUserId) {
                            try {
                              await togglePendingInvoice(effectiveUserId, tx.id, !tx.pendingInvoice);
                            } catch (error) {
                              console.error(error);
                              alert("Error al actualizar estado");
                            }
                          }
                        }}
                        title={tx.pendingInvoice ? "Quitar pendiente" : "Marcar como pendiente de factura"}
                        className={`hidden md:flex p-2 rounded-xl transition-all ${
                          tx.pendingInvoice 
                            ? 'bg-amber-500/20 text-amber-500 hover:bg-amber-500/30' 
                            : 'bg-zinc-800 text-zinc-500 hover:text-amber-500 hover:bg-zinc-700'
                        }`}
                      >
                        <Clock size={16} />
                    </button>
                    {tx.type === 'TRANSFER' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (tx.journalEntryId) return;

                          // Armar el asiento por la contraparte de origen-destino
                          const sourceAccount = accounts.find(a => a.id === tx.details.find(d => d.type === 'DEBIT')?.accountId);
                          const destAccount = accounts.find(a => a.id === tx.details.find(d => d.type === 'CREDIT')?.accountId);
                          const amount = tx.details.find(d => d.type === 'DEBIT')?.amount || 0;

                          // Buscar cómo están mapeadas en contabilidad
                          const sourceAccDb = accountingAccounts.find(acc => acc.linkedFinanceAccountId === (sourceAccount?.id));
                          const destAccDb = accountingAccounts.find(acc => acc.linkedFinanceAccountId === (destAccount?.id));
                          
                          setProposedEntry({
                            date: tx.date,
                            description: tx.description || 'Transferencia entre cuentas propias',
                            type: 'OPERATIVO',
                            lines: [
                              { accountId: destAccDb?.id || '', debit: amount, credit: 0 },
                              { accountId: sourceAccDb?.id || '', debit: 0, credit: amount }
                            ]
                          });
                          setSettlingTransfer(tx);
                        }}
                        className={`text-xs font-bold px-3 py-1.5 border rounded-lg transition-colors flex flex-col items-center ${
                          tx.journalEntryId 
                            ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20 cursor-default uppercase tracking-widest text-[9px]'
                            : 'bg-zinc-800 text-zinc-300 border-zinc-700 hover:text-white hover:bg-zinc-700'
                        }`}
                      >
                        {tx.journalEntryId ? 'Asentado' : '🧮 Crear Asiento'}
                      </button>
                    )}
                    <div className="flex flex-col items-end">
                      <div className={`font-bold ${
                      tx.type === 'INCOME' ? 'text-emerald-500' :
                      tx.type === 'EXPENSE' ? 'text-red-500' :
                      'text-zinc-100'
                    }`}>
                      {tx.type === 'INCOME' ? '+' : tx.type === 'EXPENSE' ? '-' : ''}
                      {formatCurrency(tx.details.reduce((sum, d) => sum + d.amount, 0) / (tx.type === 'TRANSFER' ? 2 : 1))}
                      </div>
                      <div className="text-[9px] text-zinc-600 uppercase tracking-wider font-bold">
                        {tx.type}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 p-4 bg-zinc-800/30 border-t border-zinc-800 flex justify-between items-center">
          <div className="text-[10px] text-zinc-500 uppercase font-bold tracking-widest">
            Desliza para ver más
          </div>
          <div className="flex items-center gap-2">
            <button
              className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl text-xs font-medium transition-colors border border-zinc-700"
              onClick={() => handleExport('csv')}
            >
              <Download size={14} />
              CSV
            </button>
            <button
              className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl text-xs font-medium transition-colors border border-zinc-700"
              onClick={() => handleExport('txt')}
            >
              <Download size={14} />
              TXT
            </button>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {settlingTransfer && (
          <EntryFormModal
            userId={effectiveUserId!}
            accounts={accountingAccounts}
            initialData={proposedEntry}
            onClose={() => {
              setSettlingTransfer(null);
              setProposedEntry(null);
            }}
            onSuccess={async (entryId) => {
              // Update the original transaction directly inside finance
              try {
                await linkTransactionToJournalEntry(effectiveUserId!, settlingTransfer.id, entryId);
              } catch (e) {
                console.error("Failed to append entry to transfer", e);
              }
              setSettlingTransfer(null);
              setProposedEntry(null);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};
